import React, { useState, useEffect, useRef } from 'react';
import styles from './DaySelector.module.scss';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers1';
import { AdapterDayjs } from '@mui/x-date-pickers1/AdapterDayjs';
import useWindowSize from 'utils/useWindowSize';
import { useSelector } from 'react-redux';
import { getActiveSalon } from 'Containers/Auth/store/selectors';
import { formatCalendarDateRange } from './formatCalendarDate';
import { ChevronLeft, ChevronRight, RefreshCw, CalendarDays } from 'lucide-react';
import clx from 'classnames';
import { useTranslation } from 'react-i18next';
import { refreshCalendar as notifyNativeRefresh } from 'utils/exsternalCall';
import { START_DAY_FLUSH_WAIT_MS, createStartDayFlusher, createLeadingTrailingActionFlusher } from './startDayFlush';

const DaySelector = ({
  searchParams,
  setSearchParams,
  startDay,
  numberOfActiveDays,
  activeDays,
  onRefreshCalendar,
  canRefreshCalendar = true,
  refreshHighlighted = false,
  onRefreshHighlightClear,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [localStartDay, setLocalStartDay] = useState(startDay); // Stato locale per la data visualizzata
  const { isMobile } = useWindowSize();
  const { t } = useTranslation();
  const flushedStartDayRef = useRef(startDay);
  const searchParamsRef = useRef(searchParams);
  const setSearchParamsRef = useRef(setSearchParams);
  const flusherRef = useRef(null);
  const refreshFlusherRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const refreshDepsRef = useRef({
    canRefreshCalendar,
    onRefreshCalendar,
    onRefreshHighlightClear,
  });
  searchParamsRef.current = searchParams;
  setSearchParamsRef.current = setSearchParams;
  refreshDepsRef.current = {
    canRefreshCalendar,
    onRefreshCalendar,
    onRefreshHighlightClear,
  };
  if (!flusherRef.current) {
    flusherRef.current = createStartDayFlusher({
      waitMs: START_DAY_FLUSH_WAIT_MS,
      getFlushed: () => flushedStartDayRef.current,
      setFlushed: (value) => {
        flushedStartDayRef.current = value;
      },
      commit: (nextDay) => {
        searchParamsRef.current.set('startDay', nextDay);
        setSearchParamsRef.current(searchParamsRef.current);
      },
    });
  }
  if (!refreshFlusherRef.current) {
    refreshFlusherRef.current = createLeadingTrailingActionFlusher({
      waitMs: START_DAY_FLUSH_WAIT_MS,
    });
  }
  const salon = useSelector(getActiveSalon());
  const giorniVisibiliSalonePassato = +salon?.giorniVisibiliSalonePassato || 0;

  const minAllowedDate =
    giorniVisibiliSalonePassato === 0
      ? dayjs().startOf('day')
      : dayjs().subtract(giorniVisibiliSalonePassato, 'day').startOf('day');

  const canGoBack = !dayjs(localStartDay)
    .subtract(numberOfActiveDays, 'day')
    .isBefore(minAllowedDate, 'day');

  useEffect(() => {
    flushedStartDayRef.current = startDay;
    if (!flusherRef.current.hasPendingTimer()) {
      setLocalStartDay(startDay);
    }
  }, [startDay]);

  useEffect(() => {
    return () => {
      flusherRef.current?.dispose();
      refreshFlusherRef.current?.dispose();
    };
  }, []);

  const handleDateChange = (newDate) => {
    if (dayjs(newDate).isBefore(minAllowedDate, 'day')) return;
    const nextDay = dayjs(newDate).format('YYYY-MM-DD');
    setLocalStartDay(nextDay);
    flusherRef.current.schedule(nextDay);
  };

  const localActiveDays = Array.from({ length: numberOfActiveDays }, (_, index) =>
    dayjs(localStartDay).add(index, 'day').format('YYYY-MM-DD')
  );

  const showHeaderRefresh = isMobile && typeof onRefreshCalendar === 'function';

  const runRefresh = () => {
    const {
      canRefreshCalendar: canRefresh,
      onRefreshCalendar: refreshFn,
      onRefreshHighlightClear: clearHighlight,
    } = refreshDepsRef.current;
    if (isRefreshingRef.current || !canRefresh || !refreshFn) return;
    isRefreshingRef.current = true;
    setIsRefreshing(true);
    notifyNativeRefresh(refreshFn);
    clearHighlight?.();
    window.setTimeout(() => {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
    }, 800);
  };

  const handleRefresh = () => {
    refreshFlusherRef.current?.schedule(runRefresh);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        open={isOpen}
        onClose={() => setIsOpen(false)}
        renderInput={({ inputRef, inputProps, InputProps }) => {
          return (
            <div
              className={clx(styles.calendar_day_selector_wrap, {
                [styles.calendar_day_selector_wrap_mobile]: isMobile,
              })}
              ref={inputRef}
            >
              <div className={styles.calendar_day_selector}>
                <button
                  type="button"
                  disabled={!canGoBack}
                  className={styles.calendar_arrow_button}
                  onClick={() => {
                    const newDate = dayjs(localStartDay).subtract(+numberOfActiveDays, 'day').format('YYYY-MM-DD');
                    handleDateChange(newDate);
                  }}
                  aria-label="Giorno precedente"
                >
                  <ChevronLeft size={16} strokeWidth={2.4} />
                </button>
                <button
                  type="button"
                  className={clx(styles.day_value, {
                    [styles.day_value_open]: isOpen,
                  })}
                  onClick={() => setIsOpen(!isOpen)}
                  aria-haspopup="dialog"
                  aria-expanded={isOpen}
                  aria-label={t('scheduler.calendarHeader.selectDate')}
                  title={t('scheduler.calendarHeader.selectDateHint')}
                >
                  <CalendarDays
                    className={styles.day_value_icon}
                    size={14}
                    strokeWidth={2.3}
                    aria-hidden
                  />
                  <span className={styles.day_value_text}>
                    {formatCalendarDateRange(localActiveDays, isMobile)}
                  </span>
                </button>
                <button
                  type="button"
                  className={styles.calendar_arrow_button}
                  onClick={() => {
                    const newDate = dayjs(localStartDay).add(numberOfActiveDays, 'day').format('YYYY-MM-DD');
                    handleDateChange(newDate);
                  }}
                  aria-label="Giorno successivo"
                >
                  <ChevronRight size={16} strokeWidth={2.4} />
                </button>
              </div>
              {showHeaderRefresh ? (
                <button
                  type="button"
                  disabled={!canRefreshCalendar || isRefreshing}
                  className={clx(styles.calendar_refresh_button, {
                    [styles.calendar_refresh_button_spinning]: isRefreshing,
                    [styles.calendar_refresh_button_disabled]: !canRefreshCalendar,
                    [styles.calendar_refresh_button_highlight]:
                      refreshHighlighted && !isRefreshing,
                  })}
                  onClick={handleRefresh}
                  aria-label={t('scheduler.refreshFab.ariaLabel')}
                  title={
                    refreshHighlighted
                      ? t('scheduler.refreshFab.staleHint')
                      : t('scheduler.refreshFab.ariaLabel')
                  }
                >
                  <RefreshCw className={styles.refresh_icon} size={16} strokeWidth={2.4} />
                </button>
              ) : null}
            </div>
          );
        }}
        label='DatePicker'
        value={dayjs(localActiveDays[0], 'YYYY-MM-DD')}
        minDate={minAllowedDate}
        ampm={false}
        onChange={(newValue) => {
          // Opzione A: il giorno cliccato è lo startDay (range di N giorni da lì in poi)
          const newStartDay = dayjs(newValue);
          if (newStartDay.isBefore(minAllowedDate, 'day')) return;
          handleDateChange(newStartDay.format('YYYY-MM-DD'));
        }}
      />
    </LocalizationProvider>
  );
};

export default DaySelector;
